import { useEffect } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import type { PlanningFrame } from '../../types/planning';
import { parseUtcTimestamp } from '../../utils/timeUtils';
import { fmt } from '../../utils/numberFormat';

const formatTs = (iso?: string) =>
  iso ? parseUtcTimestamp(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

/** Local wall-clock `datetime-local` value → UTC ISO instant. */
const localInputToUtcIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** UTC ISO instant → local `datetime-local` value (minute precision). */
const utcIsoToLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = parseUtcTimestamp(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Base case of a planning run: which observed frame the scenario is put on
 * (HISTORICAL_PLAYBACK.md → "Zweite Rolle"). Synthetic is the labelled
 * fallback, never the default.
 */
export function BaseCaseCard({ hasResults }: { hasResults: boolean }) {
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const baseCase = useStressTestStore(s => s.baseCase);
  const setBaseCase = useStressTestStore(s => s.setBaseCase);
  const availability = useStressTestStore(s => s.availability);
  const loadAvailability = useStressTestStore(s => s.loadAvailability);
  const isControlActive = useStressTestStore(s => s.isControlActive);
  const setControlActive = useStressTestStore(s => s.setControlActive);

  useEffect(() => {
    if (stressZoneId) {
      void loadAvailability(stressZoneId);
    }
  }, [stressZoneId, loadAvailability]);

  const hasArchive = availability?.hasArchive ?? false;
  const archiveMode = baseCase.mode === 'archive';

  const frameOptions: { key: PlanningFrame; label: string; hint: string }[] = [
    {
      key: 'peakLoad',
      label: 'Starklast-Frame',
      hint: availability?.peakLoadFrameAt
        ? `${formatTs(availability.peakLoadFrameAt)} · Trafo ${fmt(availability.peakLoadTrafoLoadingPct ?? 0, 0)} %, schlechtestes Kabel ${fmt(availability.peakLoadWorstLineLoadingPct ?? 0, 0)} %`
        : 'schwerster Bezugs-Frame der letzten 12 Monate',
    },
    {
      key: 'feedIn',
      label: 'Einspeise-Frame',
      hint: availability?.feedInFrameAt
        ? `${formatTs(availability.feedInFrameAt)} · Trafo ${fmt(availability.feedInTrafoPowerKw ?? 0, 0)} kW, schlechtestes Kabel ${fmt(availability.feedInWorstLineLoadingPct ?? 0, 0)} %`
        : 'schwerster Rückspeise-Frame (bzw. leichteste Last)',
    },
    {
      key: 'timestamp',
      label: 'Zeitpunkt',
      hint: 'nächster archivierter Frame vor dem gewählten Zeitpunkt',
    },
  ];

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden ${hasResults ? 'opacity-90' : ''}`}>
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Grundzustand</h3>
          {availability && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              hasArchive
                ? (availability.seasoned ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300')
                : 'bg-gray-100 text-gray-600 dark:bg-primary-600 dark:text-primary-200'
            }`}>
              {hasArchive
                ? `Archiv ${formatTs(availability.from).split(',')[0]} – ${formatTs(availability.to).split(',')[0]}${availability.seasoned ? '' : ' · < 52 Wochen'}`
                : 'kein Archiv'}
            </span>
          )}
        </div>

        {/* Mode */}
        <div className="flex rounded-lg border border-gray-300 dark:border-primary-500 overflow-hidden text-xs mb-2">
          <button
            type="button"
            onClick={() => setBaseCase({ mode: 'archive' })}
            className={`flex-1 px-2 py-1.5 font-medium transition-colors ${archiveMode ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
          >
            Beobachtet (Archiv)
          </button>
          <button
            type="button"
            onClick={() => setBaseCase({ mode: 'synthetic', frame: baseCase.frame === 'timestamp' ? 'peakLoad' : baseCase.frame })}
            className={`flex-1 px-2 py-1.5 font-medium transition-colors ${!archiveMode ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
          >
            Synthetisch
          </button>
        </div>

        {archiveMode && !hasArchive && availability && (
          <p className="text-[11px] text-amber-700 dark:text-amber-300 mb-2">
            Diese Zone hat noch kein Lastfluss-Archiv. Der Lauf fällt gekennzeichnet auf den synthetischen Worst Case zurück.
          </p>
        )}

        {/* Frame / direction */}
        <div className="space-y-1">
          {frameOptions
            .filter(o => archiveMode || o.key !== 'timestamp')
            .map(o => (
              <label key={o.key} className={`flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer border ${
                baseCase.frame === o.key
                  ? 'border-accent-primary bg-accent-primary/5 dark:bg-accent-primary/10'
                  : 'border-transparent hover:bg-gray-50 dark:hover:bg-primary-600'
              }`}>
                <input
                  type="radio"
                  name="planning-frame"
                  className="mt-0.5"
                  checked={baseCase.frame === o.key}
                  onChange={() => setBaseCase({ frame: o.key })}
                />
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-gray-800 dark:text-primary-100">
                    {archiveMode ? o.label : (o.key === 'peakLoad' ? 'Starklast (Worst Case)' : 'Einspeisung (Worst Case)')}
                  </span>
                  <span className="block text-[10px] text-gray-500 dark:text-primary-400 leading-snug">
                    {archiveMode ? o.hint : (o.key === 'peakLoad' ? 'Haushalte g(1)·Jahresmittel + Geräte mit Nennleistung' : 'Schwachlast 0,3·Jahresmittel, PV mit Nennleistung')}
                  </span>
                </span>
              </label>
            ))}
        </div>

        {archiveMode && baseCase.frame === 'timestamp' && (
          <div className="mt-2">
            <input
              type="datetime-local"
              value={utcIsoToLocalInput(baseCase.timestamp)}
              min={utcIsoToLocalInput(availability?.from)}
              max={utcIsoToLocalInput(availability?.to)}
              onChange={(e) => setBaseCase({ timestamp: localInputToUtcIso(e.target.value) })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
            />
            <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1">Ortszeit; die Richtung (Bezug/Einspeisung) wird aus dem Frame abgeleitet.</p>
          </div>
        )}

        {/* §14a */}
        <label className="mt-3 flex items-center gap-2 text-xs text-gray-700 dark:text-primary-200 cursor-pointer">
          <input type="checkbox" checked={isControlActive} onChange={(e) => setControlActive(e.target.checked)} />
          <span>§14a-Steuerung aktiv <span className="text-[10px] text-gray-500 dark:text-primary-400">(steuerbare Lasten auf P_min gedimmt)</span></span>
        </label>
      </div>
    </div>
  );
}
